/**
 * Browser Compatibility Utilities
 * Ensures cross-browser compatibility for all supported platforms
 */

// Polyfill for older browsers that don't support Array.flatMap
if (!Array.prototype.flatMap) {
  Array.prototype.flatMap = function(callback, thisArg) {
    return this.reduce(function(acc, currentValue, index, array) {
      var result = callback.call(thisArg, currentValue, index, array);
      return acc.concat(Array.isArray(result) ? result : [result]);
    }, []);
  };
}

// Polyfill for Object.values (IE support)
if (!Object.values) {
  Object.values = function(obj) {
    var values = [];
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        values.push(obj[key]);
      }
    }
    return values;
  };
}

// Polyfill for Object.entries (IE support)
if (!Object.entries) {
  Object.entries = function(obj) {
    var entries = [];
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) {
        entries.push([key, obj[key]]);
      }
    }
    return entries;
  };
}

// Safe localStorage wrapper
export const safeLocalStorage = {
  getItem: function(key) {
    try {
      return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
    } catch (e) {
      console.warn('localStorage not available:', e);
      return null;
    }
  },
  
  setItem: function(key, value) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  },
  
  removeItem: function(key) {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(key);
      }
    } catch (e) {
      console.warn('localStorage not available:', e);
    }
  }
};

// Safe sessionStorage wrapper
export const safeSessionStorage = {
  getItem: function(key) {
    try {
      return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
    } catch (e) {
      console.warn('sessionStorage not available:', e);
      return null;
    }
  },
  
  setItem: function(key, value) {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(key, value);
      }
    } catch (e) {
      console.warn('sessionStorage not available:', e);
    }
  },
  
  removeItem: function(key) {
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(key);
      }
    } catch (e) {
      console.warn('sessionStorage not available:', e);
    }
  }
};

// Cross-browser date parsing
export const safeDateParse = function(dateString) {
  if (!dateString) return null;
  
  try {
    // Handle different date formats
    let date;
    
    // Try standard ISO format first
    date = new Date(dateString);
    
    // If invalid, try parsing manually for common formats
    if (isNaN(date.getTime())) {
      // Try MM/DD/YYYY format
      const parts = dateString.split('/');
      if (parts.length === 3) {
        date = new Date(parts[2], parts[0] - 1, parts[1]);
      }
    }
    
    // Final validation
    if (isNaN(date.getTime())) {
      console.warn('Unable to parse date:', dateString);
      return new Date(); // Fallback to current date
    }
    
    return date;
  } catch (error) {
    console.warn('Date parsing error:', error);
    return new Date(); // Fallback to current date
  }
};

// Cross-browser console wrapper
export const safeConsole = {
  log: function(...args) {
    if (typeof console !== 'undefined' && console.log) {
      try {
        console.log(...args);
      } catch (e) {
        // Silently fail if console is not available
      }
    }
  },
  
  warn: function(...args) {
    if (typeof console !== 'undefined' && console.warn) {
      try {
        console.warn(...args);
      } catch (e) {
        // Silently fail if console is not available
      }
    }
  },
  
  error: function(...args) {
    if (typeof console !== 'undefined' && console.error) {
      try {
        console.error(...args);
      } catch (e) {
        // Silently fail if console is not available
      }
    }
  }
};

// Detect browser capabilities
export const browserCapabilities = {
  hasLocalStorage: (function() {
    try {
      return typeof localStorage !== 'undefined' && localStorage !== null;
    } catch (e) {
      return false;
    }
  })(),
  
  hasSessionStorage: (function() {
    try {
      return typeof sessionStorage !== 'undefined' && sessionStorage !== null;
    } catch (e) {
      return false;
    }
  })(),
  
  hasConsole: typeof console !== 'undefined',
  
  hasNavigator: typeof navigator !== 'undefined',
  
  isMobile: (function() {
    try {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        typeof navigator !== 'undefined' ? navigator.userAgent : ''
      );
    } catch (e) {
      return false;
    }
  })(),
  
  isTouchDevice: (function() {
    try {
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    } catch (e) {
      return false;
    }
  })()
};

// Cross-browser date formatting
export const formatDate = function(date, options = {}) {
  if (!date) return '';
  
  try {
    const dateObj = typeof date === 'string' ? safeDateParse(date) : date;
    if (!dateObj || isNaN(dateObj.getTime())) return '';
    
    // Default formatting options
    const defaultOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      ...options
    };
    
    // Use Intl.DateTimeFormat for cross-browser compatibility
    if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
    }
    
    // Fallback for older browsers
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    const month = months[dateObj.getMonth()];
    const day = dateObj.getDate();
    const year = dateObj.getFullYear();
    
    return `${month} ${day}, ${year}`;
  } catch (error) {
    safeConsole.error('Date formatting error:', error);
    return '';
  }
};

// Cross-browser clipboard functionality
export const copyToClipboard = function(text) {
  if (!text) {
    console.warn('No text provided to copyToClipboard');
    return Promise.resolve(false);
  }

  // Modern browsers with clipboard API
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text)
      .then(() => {
        safeConsole.log('Text copied to clipboard successfully');
        return true;
      })
      .catch((error) => {
        safeConsole.error('Failed to copy text to clipboard:', error);
        return false;
      });
  }

  // Fallback for older browsers
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      safeConsole.log('Text copied to clipboard using fallback method');
      return Promise.resolve(true);
    } else {
      safeConsole.error('Failed to copy text using fallback method');
      return Promise.resolve(false);
    }
  } catch (error) {
    safeConsole.error('Clipboard fallback failed:', error);
    return Promise.resolve(false);
  }
};

// Initialize compatibility features
export const initBrowserCompatibility = function() {
  // Log browser capabilities for debugging
  safeConsole.log('🌐 Browser Capabilities:', browserCapabilities);
  
  // Apply mobile-specific fixes
  if (browserCapabilities.isMobile) {
    // Prevent zoom on input focus (iOS)
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute('content', 
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'
      );
    }
  }
};

export default {
  safeLocalStorage,
  safeSessionStorage,
  safeDateParse,
  safeConsole,
  browserCapabilities,
  formatDate,
  copyToClipboard,
  initBrowserCompatibility
};